import { toast } from '@parsleyai/ui'
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { callSubmitMealPlanRequest } from '@/services/firebase/functions'
import { usePushNotifications } from '@/store/push/usePushNotifications'
import { useGetMealPlanRequests } from '@/store/userData/mealPlanRequests/useGetMealPlanRequests'
import { useGetMealPlanResults } from '@/store/userData/mealPlanResults/useGetMealPlanResults'
import { MealPlanLifecycle, MealPlanRequestStatus, mealCount } from '@/types/mealPlan'
import { isOutcomeEdge } from './announce'
import { type PlanGenContextValue, PlanGenPhase } from './types'

export const PlanGenContext = createContext<PlanGenContextValue | undefined>(undefined)

// Failed requests are terminal on the (backend-owned) request doc, so "dismiss"
// can't clear server state — we remember dismissed request ids locally instead.
const DISMISSED_FAILED_KEY = 'parsley.planGen.dismissedFailedRequestIds'

// One-time "want a push when it's ready?" nudge, fired the first time a plan starts
// generating. Shown at most once ever — Settings → Notifications is the durable
// opt-in — so a single persisted boolean flag is enough.
const PUSH_NUDGE_SHOWN_KEY = 'parsley.pushNotif.nudgeShown'

function readStringList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

const TOAST_DURATION_MS = 7000

interface PlanGenProviderProps {
  children: ReactNode
}

// Headless controller for the app-wide "new plan in progress" indicator. It owns no
// Firestore subscription of its own — it derives the lifecycle phase from the two
// existing slices (the newest request + its matching result) so the same signal
// drives the nav pill, the toasts, and (later) the "New plan" buttons. Because it
// reads from current backend state, a page refresh mid-run restores the right phase.
export function PlanGenProvider({ children }: PlanGenProviderProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { latestRequest } = useGetMealPlanRequests()
  const { state: resultsState } = useGetMealPlanResults()
  const { supported: pushSupported, permission: pushPermission, standalone, isIOS, enable: enablePush } = usePushNotifications()

  const [dismissedFailedIds, setDismissedFailedIds] = useState<string[]>(() => readStringList(DISMISSED_FAILED_KEY))

  // Progress-popover UI state lives here, in the persistent provider, rather than in PlanPill.
  // PlanPill remounts on every navigation (PageLayout is rendered per-page), so component-local
  // state would reset and re-fire the auto-open on each route change. Hoisting it keeps a user's
  // close/minimize sticky across navigation and keeps the two PlanPill mounts (nav + top bar) in sync.
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [popoverCollapsed, setPopoverCollapsed] = useState(false)

  // The draft whose Ready pill has already played its one-time bounce-in. Owned here (not in
  // PlanPill, which remounts per route) so the pop fires once when the plan lands and the pill
  // then sits still on every subsequent navigation. A new plan carries a new draftId, so the
  // `!== draftId` check below re-arms the pop automatically — no explicit reset needed.
  const [poppedReadyDraftId, setPoppedReadyDraftId] = useState<string | null>(null)
  const markReadyPillPopped = useCallback((id: string) => setPoppedReadyDraftId(id), [])

  // The result doc for the newest request, matched by planId (absent while the
  // request is still processing, since the result lands only on success).
  const matchedResult = useMemo(() => {
    if (!latestRequest?.planId) return null
    return resultsState.mealPlanResults.find((result) => result.planId === latestRequest.planId) ?? null
  }, [latestRequest?.planId, resultsState.mealPlanResults])

  const phase = useMemo<PlanGenPhase>(() => {
    if (!latestRequest) return PlanGenPhase.Idle
    switch (latestRequest.status) {
      case MealPlanRequestStatus.Pending:
      case MealPlanRequestStatus.Processing:
        return PlanGenPhase.Processing
      case MealPlanRequestStatus.Completed:
        // "Ready" lasts only while the generated plan is still an un-engaged draft;
        // finalizing it (lifecycle → active/complete) returns the indicator to idle.
        return matchedResult?.lifecycle === MealPlanLifecycle.Draft ? PlanGenPhase.Ready : PlanGenPhase.Idle
      case MealPlanRequestStatus.Failed:
        return dismissedFailedIds.includes(latestRequest.id) ? PlanGenPhase.Idle : PlanGenPhase.Failed
      default:
        return PlanGenPhase.Idle
    }
  }, [latestRequest, matchedResult, dismissedFailedIds])

  // Auto-open the progress popover once when a plan enters Processing, and reset the
  // transient popover state on any other phase transition. This effect runs only when `phase`
  // actually changes value — and because it lives in the persistent provider (not PlanPill,
  // which remounts per navigation), it fires on the real idle→processing transition, NOT on every
  // page change. That's what keeps a user's close/minimize sticky: navigating no longer re-opens it.
  useEffect(() => {
    setPopoverCollapsed(false)
    setPopoverOpen(phase === PlanGenPhase.Processing)
  }, [phase])

  // Elapsed clock — ticks once a second only while processing. Anchored to the
  // server's processingStartedAt (falling back to createdAt) so it survives refresh.
  const startedAtMs = useMemo(() => {
    const startedAt = latestRequest?.processingStartedAt ?? latestRequest?.createdAt
    return startedAt ? startedAt.toMillis() : null
  }, [latestRequest?.processingStartedAt, latestRequest?.createdAt])

  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    if (phase !== PlanGenPhase.Processing) return
    setNowMs(Date.now())
    const id = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(id)
  }, [phase])
  const elapsedSeconds = phase === PlanGenPhase.Processing && startedAtMs ? Math.max(0, Math.floor((nowMs - startedAtMs) / 1000)) : 0

  const planName = matchedResult?.name?.trim() || t('planGen.defaultPlanName')
  const draftId = latestRequest?.planId ?? null
  const dinnerCount = matchedResult ? mealCount(matchedResult) : 0

  // Pop the Ready pill only while this draft is Ready AND it hasn't popped yet.
  const shouldPopReadyPill = phase === PlanGenPhase.Ready && draftId !== null && poppedReadyDraftId !== draftId

  const review = useCallback(() => {
    // Deep-link straight to the draft's finalize/review screen. draftId is always set
    // while a plan is Ready (the only phase these affordances render in); the history
    // fallback is purely defensive.
    if (draftId) {
      navigate(`/meal-plans/${draftId}/edit`)
      return
    }
    navigate('/meal-plans/history')
  }, [navigate, draftId])

  const retry = useCallback(() => {
    if (!latestRequest) return
    // Re-submits the original intent. The week's effort answer isn't stored on the request, so a
    // retry falls back to the user's saved Eating Style preference. The profile selection IS
    // replayed: silently re-applying a profile the user switched off would put ingredients they
    // excluded into the new plan. Omitted, never undefined.
    callSubmitMealPlanRequest({
      daysRequested: latestRequest.daysRequested,
      servings: latestRequest.servings,
      levelUpsEnabled: latestRequest.levelUpsEnabled,
      ...(latestRequest.recipeMix ? { recipeMix: latestRequest.recipeMix } : {}),
      ...(latestRequest.activeProfileIds ? { activeProfileIds: latestRequest.activeProfileIds } : {})
    }).catch((error) => {
      console.error('Failed to re-submit meal plan request:', error)
    })
  }, [latestRequest])

  const dismissFailed = useCallback(() => {
    if (!latestRequest) return
    setDismissedFailedIds((prev) => {
      if (prev.includes(latestRequest.id)) return prev
      const next = [...prev, latestRequest.id]
      try {
        localStorage.setItem(DISMISSED_FAILED_KEY, JSON.stringify(next))
      } catch {
        // localStorage unavailable (private mode / quota) — dismissal stays in-memory.
      }
      return next
    })
  }, [latestRequest])

  const getHelp = useCallback(() => {
    // Routing gap: no support destination decided yet. Intentional noop for now.
  }, [])

  const pushNudgeShownRef = useRef<boolean>(readFlag(PUSH_NUDGE_SHOWN_KEY))

  // Announce an outcome on the transition into it, never on the standing condition — see
  // {@link isOutcomeEdge}. Nothing is persisted: a reload, a second tab and a fresh sign-in all
  // start with no previous phase, so none of them re-announce a plan already sitting in draft.
  const prevPhaseRef = useRef<PlanGenPhase | null>(null)

  useEffect(() => {
    const previous = prevPhaseRef.current
    prevPhaseRef.current = phase
    if (!isOutcomeEdge(previous, phase)) return

    if (phase === PlanGenPhase.Ready && draftId) {
      toast.success(t('planGen.ready.toast.title'), {
        id: `plan-ready:${draftId}`,
        description: t('planGen.ready.toast.description', { name: planName, count: dinnerCount }),
        action: { label: t('planGen.ready.toast.action'), onClick: review },
        duration: TOAST_DURATION_MS
      })
    } else if (phase === PlanGenPhase.Failed && latestRequest) {
      toast.error(t('planGen.failed.toast.title'), {
        id: `plan-failed:${latestRequest.id}`,
        description: t('planGen.failed.toast.description'),
        action: { label: t('planGen.failed.toast.action'), onClick: retry },
        duration: TOAST_DURATION_MS
      })
    }
  }, [phase, draftId, planName, dinnerCount, latestRequest, review, retry, t])

  // One-time nudge: the first time a plan starts generating, offer a push opt-in so
  // the user can walk away instead of watching the pill. Only for users who haven't
  // decided yet ('default' permission excludes both already-granted/subscribed and
  // blocked), never on an uninstalled iOS PWA (Settings shows the Add-to-Home-Screen
  // path there instead), and at most once ever. The canonical opt-in stays in Settings.
  useEffect(() => {
    if (phase !== PlanGenPhase.Processing || pushNudgeShownRef.current) return
    const iosNeedsInstall = isIOS && !standalone
    if (!pushSupported || pushPermission !== 'default' || iosNeedsInstall) return

    pushNudgeShownRef.current = true
    try {
      localStorage.setItem(PUSH_NUDGE_SHOWN_KEY, 'true')
    } catch {
      // localStorage unavailable — the ref still prevents a repeat this session.
    }
    toast(t('planGen.processing.notify.toast.title'), {
      description: t('planGen.processing.notify.toast.description'),
      action: {
        label: t('planGen.processing.notify.toast.action'),
        onClick: () => {
          enablePush()
            .then((enabled) => {
              if (enabled) toast.success(t('settings.notifications.toast.on'), { id: 'push-notifications' })
            })
            .catch(() => toast.error(t('settings.notifications.toast.error'), { id: 'push-notifications' }))
        }
      },
      duration: 12000
    })
  }, [phase, pushSupported, pushPermission, isIOS, standalone, enablePush, t])

  const value = useMemo<PlanGenContextValue>(
    () => ({
      phase,
      elapsedSeconds,
      planName,
      draftId,
      dinnerCount,
      review,
      retry,
      dismissFailed,
      getHelp,
      popoverOpen,
      setPopoverOpen,
      popoverCollapsed,
      setPopoverCollapsed,
      shouldPopReadyPill,
      markReadyPillPopped
    }),
    [
      phase,
      elapsedSeconds,
      planName,
      draftId,
      dinnerCount,
      review,
      retry,
      dismissFailed,
      getHelp,
      popoverOpen,
      popoverCollapsed,
      shouldPopReadyPill,
      markReadyPillPopped
    ]
  )

  return <PlanGenContext.Provider value={value}>{children}</PlanGenContext.Provider>
}
